import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Audit-log viewer. Paginated feed of every admin action: bans, plan
 * changes, refund intents, support replies, password rotations, admin
 * invites — whatever {@code AuditLogService.record} captured.
 *
 * Endpoints (both gated to SUPERADMIN-only server-side):
 *
 *   GET /api/admin/audit?page=N&size=50 →
 *     { items: AuditRow[], total, page, size, hasNext, countLast24h }
 *
 *   AuditRow shape:
 *     { id, actorId, actorEmail, action, target, diff, ip, createdAt }
 *
 * Layout:
 *   - 3 KPI tiles (24h count, total all-time, distinct actors)
 *   - Search + action-filter strip
 *   - Table: When / Actor / Action / Target / IP / Diff toggle
 *   - Expand a row to see the raw diff JSON in a monospace block
 *   - "Load more" appends the next page so the operator can scroll
 *     without losing context (vs paginated controls that re-render)
 *
 * Why not infinite-scroll on intersection observer? Audit forensics
 * is usually targeted at a specific date or actor — manual "Load
 * more" gives the operator control + matches the rest of the
 * console's pagination pattern.
 *
 * @author <lukman@zshift.io>
 */
const PAGE_SIZE = 50;

export function AuditLogs() {
  const { toast } = useUI();

  const [items, setItems]       = useState([]);
  const [page, setPage]         = useState(0);
  const [hasNext, setHasNext]   = useState(false);
  const [total, setTotal]       = useState(0);
  const [count24h, setCount24h] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [appending, setAppending] = useState(false);

  // Free-text filter (matches actor email, action code, or target).
  // Applied locally to the already-loaded set — cheap because audit
  // rows are small and we cap the fetched window. For deeper searches
  // an operator would load more pages.
  const [query, setQuery] = useState('');

  // Track which rows have their diff JSON expanded.
  const [expanded, setExpanded] = useState(() => new Set());

  // ── Loader ─────────────────────────────────────────────────────
  const load = useCallback(async (pageIdx, mode /* 'replace' | 'append' */) => {
    if (mode === 'append') setAppending(true);
    else setLoading(true);
    try {
      const res = await api(`/api/admin/audit?page=${pageIdx}&size=${PAGE_SIZE}`);
      const rows = Array.isArray(res?.items) ? res.items : [];
      setItems((prev) => (mode === 'append' ? prev.concat(rows) : rows));
      setPage(pageIdx);
      setHasNext(!!res?.hasNext);
      setTotal(Number(res?.total || 0));
      setCount24h(Number(res?.countLast24h || 0));
    } catch (e) {
      // 403 means the signed-in admin isn't Superadmin — route guard
      // should normally prevent this from rendering, but if someone
      // gets here via deep link surface the message rather than
      // silently blanking the page.
      const status = e?.status;
      if (status === 403) {
        toast.error('Audit logs are restricted to Superadmin accounts.');
      } else {
        toast.error(e?.message || 'Could not load audit logs.');
      }
    } finally {
      setLoading(false);
      setAppending(false);
    }
  }, [toast]);

  useEffect(() => { load(0, 'replace'); }, [load]);

  // ── Derived ────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) => {
      const fields = [
        a.actorEmail, a.action, a.target, a.ip, String(a.actorId ?? ''),
      ];
      return fields.some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [items, query]);

  const distinctActors = useMemo(() => {
    const s = new Set();
    for (const a of items) {
      if (a.actorEmail) s.add(a.actorEmail);
      else if (a.actorId != null) s.add('admin#' + a.actorId);
    }
    return s.size;
  }, [items]);

  // Toggle the row's diff expansion.
  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Audit <em>logs</em></h1>
          <p className="page-sub">
            Every admin action with full diff and source IP. Sensitive — restricted
            to Superadmins.
          </p>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => load(0, 'replace')}
          disabled={loading}
          title="Re-fetch from page 0"
        >
          <Icon name="refresh" size={13} />
          <span style={{ marginLeft: 6 }}>{loading ? 'Loading…' : 'Refresh'}</span>
        </button>
      </div>

      {/* ── KPI strip ─────────────────────────────────────────── */}
      <div className="row r-1-1" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <StatTile
          label="Actions · last 24h"
          value={count24h}
          loading={loading}
          icon="logs"
          color="orange"
        />
        <StatTile
          label="All-time actions"
          value={total}
          loading={loading}
          icon="shield"
          color="blue"
        />
        <StatTile
          label="Loaded · distinct actors"
          value={distinctActors}
          loading={loading}
          icon="users"
          color="green"
          sub={`${items.length.toLocaleString()} rows in view`}
        />
      </div>

      {/* ── Table card ────────────────────────────────────────── */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Activity feed</h3>
            <div className="sub">Newest first · {filteredItems.length.toLocaleString()} of {items.length.toLocaleString()} loaded</div>
          </div>

          {/* Search bar — local filter only. For deeper queries, Load more
              pulls older windows. */}
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Icon name="search" size={14} style={{ color: 'var(--ink-3)' }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter loaded rows — by email, action, target, or IP"
              aria-label="Filter audit log rows"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                fontSize: 13,
                color: 'var(--ink)',
                padding: '4px 0',
              }}
            />
            {query && (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setQuery('')}
                style={{ padding: '4px 8px' }}
              >
                Clear
              </button>
            )}
          </div>

          <div className="table-wrap" style={{ maxHeight: 640, overflowY: 'auto' }}>
            <table className="t">
              <thead>
                <tr>
                  <th style={stickyTh(120)}>When</th>
                  <th style={stickyTh()}>Actor</th>
                  <th style={stickyTh()}>Action</th>
                  <th style={stickyTh()}>Target</th>
                  <th style={stickyTh(140)}>IP</th>
                  <th style={{ ...stickyTh(40), textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
                )}
                {!loading && filteredItems.length === 0 && items.length === 0 && (
                  <tr><td colSpan={6} style={emptyCell}>
                    No audit entries yet. The first admin action will land here.
                  </td></tr>
                )}
                {!loading && filteredItems.length === 0 && items.length > 0 && (
                  <tr><td colSpan={6} style={emptyCell}>
                    No rows match <strong>"{query}"</strong>. Load more or clear the filter.
                  </td></tr>
                )}
                {!loading && filteredItems.map((a) => {
                  const isExpanded = expanded.has(a.id);
                  const verb = humanizeAction(a.action);
                  const hasDiff = a.diff && Object.keys(a.diff).length > 0;
                  return (
                    // Fragment carries the key for the row pair so
                    // React's reconciler doesn't warn — the inner
                    // <tr> keys are kept for clarity when inspecting
                    // the DOM but the Fragment's key is what matters
                    // to React's list-diffing.
                    <Fragment key={a.id}>
                      <tr>
                        <td className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                          <div>{agoString(a.createdAt)}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                            {formatDateTime(a.createdAt)}
                          </div>
                        </td>
                        <td>
                          <strong>{a.actorEmail || (a.actorId != null ? `admin#${a.actorId}` : 'system')}</strong>
                        </td>
                        <td>
                          <span className="mono" style={{
                            fontSize: 12,
                            padding: '2px 6px',
                            background: 'var(--bg-2)',
                            borderRadius: 4,
                            color: 'var(--ink-2)',
                          }}>
                            {a.action}
                          </span>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                            {verb}
                          </div>
                        </td>
                        <td className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                          {a.target || '—'}
                        </td>
                        <td className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                          {a.ip || '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {hasDiff ? (
                            <button
                              type="button"
                              className="btn ghost sm"
                              onClick={() => toggleRow(a.id)}
                              title={isExpanded ? 'Hide diff' : 'Show diff'}
                              style={{ padding: '4px 8px' }}
                            >
                              {isExpanded ? '−' : '+'}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {isExpanded && hasDiff && (
                        <tr>
                          <td colSpan={6} style={{
                            background: 'var(--bg-2)',
                            padding: '10px 16px',
                          }}>
                            <pre style={{
                              margin: 0,
                              fontFamily: 'Geist Mono, Menlo, monospace',
                              fontSize: 11.5,
                              lineHeight: 1.55,
                              color: 'var(--ink-2)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>
                              {safeStringify(a.diff)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load-more footer — only when the server still has more
              AND the local filter isn't actively trimming. If a query
              is hiding rows the operator probably wants to widen the
              search rather than load more. */}
          {!loading && hasNext && (
            <div style={{
              padding: '14px 16px',
              borderTop: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <button
                type="button"
                className="btn ghost"
                onClick={() => load(page + 1, 'append')}
                disabled={appending}
              >
                {appending ? 'Loading…' : `Load older · page ${page + 2}`}
              </button>
            </div>
          )}
          {!loading && !hasNext && items.length > 0 && (
            <div style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--line)',
              fontSize: 12,
              color: 'var(--ink-3)',
              textAlign: 'center',
            }}>
              End of history · {items.length.toLocaleString()} rows shown
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI tile (compact, no delta) ────────────────────────────────
function StatTile({ label, value, loading, icon, color, sub }) {
  return (
    <div className="kpi">
      <div className={`label ${color || ''}`}>
        <span className="ic"><Icon name={icon} size={13} /></span>
        {label}
      </div>
      <div className="value">
        {loading ? '—' : Number(value).toLocaleString()}
        {sub && !loading && <small>{sub}</small>}
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────
const stickyTh = (width) => ({
  position: 'sticky',
  top: 0,
  background: 'var(--card)',
  zIndex: 1,
  ...(width ? { width } : {}),
});

const emptyCell = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--ink-3)',
};

/**
 * Map an action code to a one-line English verb. Mirrors the
 * Overview / SupportOverview maps but lives here too since the
 * audit-log page renders the FULL action set (including superadmin-
 * only ones the other surfaces filter out). Unknown codes fall
 * through to a humanised "some action".
 */
function humanizeAction(code) {
  const MAP = {
    admin_login:             'signed in to the console',
    bootstrap_admin:         'was seeded as the first admin',
    invite_admin:            'invited a new admin',
    change_admin_role:       "changed an admin's role",
    disable_admin:           'disabled an admin',
    enable_admin:            're-enabled an admin',
    delete_admin:            'deleted an admin',
    change_admin_password:   'changed their password',
    update_admin_profile:    'updated their profile',
    ban_user:                'banned a user',
    unban_user:              'un-banned a user',
    change_plan:             "changed a user's plan",
    reply_query:             'replied to a support ticket',
    close_query:             'closed a support ticket',
    refund_intent:           'logged a refund intent',
    edit_plan_catalog_draft: 'edited the plan catalog',
    force_plan_reconcile:    'force-ran the plan reconciler',
    plan_expired_downgrade:  'auto-downgraded a lapsed subscription',
  };
  return MAP[code] || (code || '').replace(/_/g, ' ') || 'did something';
}

function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1)  return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24)    return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30)    return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}

function formatDateTime(iso) {
  const d = new Date(iso);
  if (!d.getTime()) return '';
  // "12 Jun · 14:32"
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
       + ' · '
       + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Pretty-print the diff JSON. Defensive — older AuditLog rows in
 * production sometimes have non-object diffs (raw strings from early
 * call sites that pre-dated the Map<String,Object> contract). We
 * stringify whatever we get rather than throwing.
 */
function safeStringify(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}
